<template>
  <div>
    <page-nav-bar
      v-if="c"
      :is-sticky="true"
      :back-button="true"
      :has-actions="true"
      title="FICHE CONTACT"
      :subtitle="`${c.last_name} ${c.first_name}`">
      <template slot="breadcrumb">
        <h1 class="page-header-breadcrumb-item is-uppercase">
          <router-link :to="{ name: 'ContactList'}">
            Contacts
          </router-link>
        </h1>
        <div class="page-header-breadcrumb-separator" />
        <div
          v-if="c && c.last_name && c.first_name"
          class="page-header-breadcrumb-item">
          <span class="has-text-weight-normal has-text-grey-dark"><span class="is-uppercase">{{ c.last_name }}</span> {{ c.first_name }}</span>
        </div>
      </template>
      <router-link
        v-if="canEdit"
        id="contactEditButton"
        slot="dropdown-content"
        :to="{name: 'ContactEdit', params: {id: c.pk}}"
        class="dropdown-item">
        <span class="icon is-medium"><i class="fas fa-user-edit" /></span><span class="is-margin-left-xsmall">Modifier fiche</span>
      </router-link>
      <a
        slot="dropdown-content"
        class="dropdown-item"
        @click="openContactSearchesQuickview()">
        <span class="icon is-medium"><i class="fas fa-search-location" /></span><span class="is-margin-left-xsmall">Critère de recherche</span>
      </a>
      <a
        v-if="canUseCalendar"
        slot="dropdown-content"
        class="dropdown-item"
        @click="createRdv()">
        <span class="icon is-medium"><i class="fas fa-calendar-plus" /></span><span class="is-margin-left-xsmall">Nouvelle action</span>
      </a>
      <a
        v-if="canUseContactSharing && !contact.shared && isAdmin && isPromoteur"
        slot="dropdown-content"
        class="dropdown-item"
        @click="initContactSharing('simple')">
        <span class="icon is-medium"><i class="fas fa-people-arrows" /></span><span class="is-margin-left-xsmall">Partager le contact</span>
      </a>
      <a
        v-if="canUseContactSharing && contact.shared && isAdmin"
        slot="dropdown-content"
        class="dropdown-item"
        @click="stopContactSharing()">
        <template v-if="contact.account.pk === currentUser.account.pk">
          <span class="icon is-medium"><i class="fas fa-people-arrows" /></span><span class="is-margin-left-xsmall">Arrêter le partage du contact</span>
        </template>
        <template v-else>
          <span class="icon is-medium"><i class="fas fa-people-arrows" /></span><span class="is-margin-left-xsmall">Arrêter le partage du contact</span>
        </template>
      </a>
      <a
        v-if="(isAdminPromoteur && !contact.shared) || (isAdminCgp && canAddUsers) || isManager"
        slot="dropdown-content"
        class="dropdown-item"
        @click="openUserSelection('simple')">
        <span class="icon is-medium"><i class="fas fa-exchange" /></span><span class="is-margin-left-xsmall">Modifier l'utilisateur référent</span>
      </a>
      <a
        v-if="c.account.pk === currentUser.account.pk"
        id="contactDeleteButton"
        slot="dropdown-content"
        class="dropdown-item has-background-danger has-text-white"
        @click="deleteContact(c)">
        <span class="icon is-medium"><i class="fas fa-trash-alt" /></span><span class="is-margin-left-xsmall">Supprimer contact</span>
      </a>
    </page-nav-bar>
    <spinner-round
      v-if="loading"
      :is-loading="loading" />
    <page-content
      v-else-if="c && !loading"
      :has-no-padd-on-act-on-desktop="true"
      :has-padd-left-on-act="true"
      :has-padd-right-on-act="true"
      :has-left-col-small="true"
      :has-left-col-small-size-fixed="false"
      :has-sticky-col-left="true">
      <template slot="left-col-small">
        <div class="has-background-white is-border">
          <div class="is-padding-top is-padding-left is-padding-right is-padding-bottom-small has-text-centered">
            <swatches
              class="colorPicker has-cursor-pointer"
              v-model="contact.swatch_color"
              @input="updateColor(contact, $event)"
              :trigger-style="{width: '30px', height: '30px'}"
              :swatch-size="30"
              shapes="circles">
              <template #trigger>
                <figure-avatar
                  v-if="contact.shared"
                  :is-valid="c.is_valid"
                  :has-hover="false"
                  :icon="'fas fa-people-arrows'"
                  :color="contact.swatch_color" />
                <figure-avatar
                  v-else
                  :is-valid="c.is_valid"
                  :has-hover="false"
                  :color="contact.swatch_color" />
              </template>
            </swatches>
            <div
              v-if="c && c.last_name && c.first_name && c.nature === 'corporation' && c.corporation_name"
              class="title is-3 is-margin-top-xsmall is-marginless-bottom">
              <div class="has-text-weight-normal has-text-grey-dark">
                <span
                  class="is-uppercase">{{ c.corporation_name }}</span>
              </div>
              <div class="is-size-5 has-text-weight-light">
                <span class="is-uppercase">{{ c.last_name }}</span>
                {{ c.first_name }}
              </div>
              <div class="is-padding-top-xxsmall is-size-5 has-text-weight-light">
                <span v-if="c.birthday">{{ c.birthday | prettyAge }}</span><span
                  v-if="c.birthday && c.profession"> - </span><span
                  v-if="c.profession"
                  class="is-uppercase">{{ c.profession }}</span>
              </div>
            </div>
            <div
              v-else-if="c && c.last_name && c.first_name"
              class="title is-3 is-margin-top-xsmall is-marginless-bottom">
              <b id="contactName">{{ c.last_name }} {{ c.first_name }}</b>
              <div class="is-padding-top-xxsmall is-size-5 has-text-weight-light">
                <span v-if="c.birthday">{{ c.birthday | prettyAge }}</span><span
                  v-if="c.birthday && c.profession"> - </span><span
                  v-if="c.profession"
                  class="is-uppercase">{{ c.profession }}</span>
              </div>
            </div>
            <div class="field is-grouped is-grouped-centered is-margin-top-small">
            </div>
          </div>
          <div
            v-if="doublons && Object.keys(doublons).length !== 0"
            class="has-background-danger">
            <div class="has-text-white is-size-7 is-padding-left is-padding-top-xsmall">
              <i>DONNÉES SIMILAIRES DÉTECTÉES SUR : </i>
            </div>
            <div
              class="has-text-white is-size-7 is-padding-left is-padding-top-xsmall is-padding-bottom-xsmall">
              <router-link :to="{ name: 'DenunciationList', params: { filter: claim_list_pk, status: ['approved', 'pending'], resetFiltersForDoublon: true }}">
                <a
                  v-if="doublons.duplicate_risk !== 0"
                  class="is-size-7 has-text-white is-padding-right-xsmall">
                  <span> BASE DÉNONCIATION </span>
                  <a class="hover-text">
                    <span class="is-size-7 has-text-white">(<a class="is-size-7 has-text-white">{{ doublons.duplicate_risk || 0 }}/3 </a><i class="fas fa-info-circle"></i>)</span>
                    <span
                      class="tooltip-text"
                      id="top">{{ messages[doublons.duplicate_risk - 1] }}</span>
                  </a>
                </a>
              </router-link>
              <router-link :to="{ name: 'ContactList', params: { filter: doublons.all_pk_contact, resetFiltersForDoublon: true }}">
                <a
                  v-if="doublons.all_pk_contact_risk !== 0"
                  class="is-size-7 has-text-white">
                  <span> BASE CONTACT </span>
                  <a class="hover-text">
                    <span class="is-size-7 has-text-white">(<a class="is-size-7 has-text-white">{{ doublons.all_pk_contact_risk || 0 }}/3 </a><i class="fas fa-info-circle"></i>)</span>
                    <span
                      class="tooltip-text"
                      id="top">{{ messages_2[doublons.all_pk_contact_risk - 1] }}</span>
                  </a>
                </a>
              </router-link>
            </div>
            <!-- <div
              v-else
              class="has-text-white is-size-7 is-padding-left is-padding-top-xsmall is-padding-bottom-xsmall">
              <a
                v-if="doublons.duplicate_risk"
                class="is-size-7 has-text-white is-padding-right-xsmall">
                <span> BASE DÉNONCIATION </span>
                <a class="hover-text">
                  <span class="is-size-7 has-text-white">(<a class="is-size-7 has-text-white">{{ doublons.duplicate_risk || 0 }}/3 </a><i class="fas fa-info-circle"></i>)</span>
                  <span
                    class="tooltip-text"
                    id="top">{{ messages[doublons.duplicate_risk - 1] }}</span>
                </a>
              </a>
              <a
                v-if="doublons.all_pk_contact"
                class="is-size-7 has-text-white">
                <span> BASE CONTACT </span>
                <a class="hover-text">
                  <span class="is-size-7 has-text-white">(<a class="is-size-7 has-text-white">{{ doublons.all_pk_contact_risk || 0 }}/3 </a><i class="fas fa-info-circle"></i>)</span>
                  <span
                    class="tooltip-text"
                    id="top">{{ messages_2[doublons.all_pk_contact_risk - 1] }}</span>
                </a>
              </a>
            </div> -->
          </div>
          <div
            class="is-padding-top-small is-padding-left is-padding-right is-padding-bottom-small is-border-top has-text-centered has-status-bar--left"
            :class="`has-status-bar--draft`"
            :style="{'border-left-color': tagColor(contact.status)}">
            <div class="level is-marginless">
              <div class="level-left">
                <div class="level-item">
                  <span class="title is-5 has-text-weight-bold">Statut :</span>
                </div>
                <div class="level-item field">
                  <div class="select is-transparent is-small">
                    <select
                      v-model="contact.status"
                      @change="updateStatus">
                      <option
                        v-for="status in contactStatusList"
                        :key="status.label"
                        :value="status.value">
                        {{ status.label }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="is-padding is-border-top">
            <div class="columns is-multiline">
              <div class="column is-12 is-paddingless-bottom">
                <div class="columns is-gapless">
                  <div class="column is-size-6 is-narrow is-w80p is-margin-right-xsmall">
                    <b>Adresse :</b>
                  </div>
                  <div
                    v-if="c.address && c.address.zip_code"
                    class="column is-size-6">
                    {{ c.address.line1 }},<br>
                    {{ c.address.additional_address }}<br>
                    {{ c.address.zip_code }} {{ c.address.city }} <i>{{ c.address.country }}</i>
                  </div>
                  <div v-else>
                    NC
                  </div>
                </div>
              </div>
              <div class="column is-12 is-paddingless-bottom">
                <div class="columns is-gapless">
                  <div class="column is-size-6 is-narrow is-w80p is-margin-right-xsmall">
                    <b>Mail :</b>
                  </div>
                  <div class="column is-size-6 is-break-word">
                    <a
                      :href="`mailto:${c.email}`"
                      class="is-hover-underline">{{ c.email }}</a>
                  </div>
                </div>
              </div>
              <div class="column is-12">
                <div class="columns is-gapless">
                  <div class="column is-size-6 is-narrow is-w80p is-margin-right-xsmall">
                    <b>Téléphone :</b>
                  </div>
                  <div class="column is-size-6">
                    <phone-number :phone="c.phone" />
                  </div>
                </div>
              </div>
              <div
                v-if="canEdit"
                class="column is-12">
                <router-link
                  class="is-linkTextPicto is-size-6"
                  :to="{name: 'ContactEdit', params: {id: c.pk}}">
                  <span><i>Modifier fiche contact</i></span>
                  <span class="icon"><i class="fal fa-long-arrow-right" /></span>
                </router-link>
              </div>
            </div>
          </div>
          <div
            v-if="c.user && currentUser.account.pk === c.user.account.pk && contact.shared"
            class="columns is-multiline is-gapless is-border-top is-marginless">
            <div class="column is-12 columns is-column is-padding">
              <div class="is-size-6">
                <b>Partagé par : </b>
                <span v-if="c.account && c.account.name">
                  <router-link
                    v-if="isPromoteurDetailsActivated(c)"
                    class="is-linkTextPicto"
                    :to="{name: 'PromoteurPresentation', params: {id: c.account.pk}}">
                    <span class="">{{ c.account.name }}</span>
                  </router-link>
                  <span v-else>{{ c.account.name }}</span>
                </span>
                <span v-else>NC</span>
              </div>
            </div>
          </div>
          <div class="columns is-multiline is-gapless is-border-top is-marginless">
            <div class="column is-12 columns is-column is-padding">
              <div class="is-size-6">
                <b>Utilisateur référent : </b>
                <span v-if="c.user && c.user.last_name">
                  <router-link
                    :to="{name: 'UserVue', params: {id: c.user.pk}}"
                    class="is-hover-underline">
                    {{ c.user.last_name }} {{ c.user.first_name }}
                    <span v-if="c.user && currentUser.account.pk !== c.user.account.pk">({{ c.user.account.name }})</span>
                  </router-link>
                </span>
                <span v-else>NC</span>
              </div>
            </div>
          </div>
          <div
            v-if="contact.account.pk === currentUser.account.pk"
            class="columns is-multiline is-gapless is-border-top is-marginless">
            <div class="column is-12 columns is-column is-padding">
              <div class="is-size-6">
                <b>Origine : </b>
                <span v-if="c.origin && c.origin.name">
                  {{ c.origin.name }}
                </span>
                <span v-else>
                  <router-link
                    class="is-underline"
                    :to="{name: 'ContactEdit', params: {id: c.pk}}">
                    Sélectionner
                  </router-link>
                </span>
              </div>
            </div>
          </div>
          <div class="columns is-multiline is-gapless is-border-top is-marginless">
            <div class="column is-12 columns is-column is-padding">
              <div class="level is-mobile is-marginless is-size-6">
                <div class="level-left">
                  <div class="level-item">
                    <b>Consentement RGPD</b>
                  </div>
                  <div class="level-item">
                    <div
                      class="tag is-light has-text-black is-rounded is-border"
                      style="border-color: #24323b;">
                      <span v-if="c.rgpd_consent">Recueilli<span v-if="c.rgpd_consent_date"> le {{ c.rgpd_consent_date|dtFormat('DD/MM/YYYY') }}</span></span>
                      <span v-else>Non recueilli</span>
                    </div>
                  </div>
                </div>
                <div
                  v-if="canEdit"
                  class="level-right">
                  <div class="level-item">
                    <a
                      id="rgpdConsentEdit"
                      class="is-linkTextPicto is-size-7"
                      @click="updateRgpdConsent()">
                      <span><i>Modifier</i></span>
                    </a>
                  </div>
                </div>
              </div>
              <!-- Informations RGPD : dates de traçabilité -->
              <div
                id="rgpdInfos"
                class="is-size-7 is-padding-top-xsmall">
                <div>Créé le <b>{{ c.created_on | dtFormat('DD/MM/YYYY') }}</b></div>
                <div class="is-padding-top-xsmall">
                  Dernière modification le <b>{{ c.last_change_date | dtFormat('DD/MM/YYYY') }}</b>
                </div>
                <div class="level is-mobile is-marginless is-padding-top-xsmall">
                  <div class="level-left">
                    <div class="level-item">
                      <span>Dernier échange :&nbsp;</span>
                    </div>
                    <div class="level-item">
                      <b
                        v-if="canEdit"
                        @click.stop>
                        <date-picker
                          id="rgpdLastInteractionPicker"
                          :text="'Aucun'"
                          :from-option="true"
                          :model="c.last_interaction_date"
                          :style-version="4"
                          :clearable="false"
                          @input="updateLastInteractionDate($event)"></date-picker>
                      </b>
                      <b v-else-if="c.last_interaction_date">{{ c.last_interaction_date | dtFormat('DD/MM/YYYY') }}</b>
                      <b v-else>Aucun</b>
                    </div>
                  </div>
                </div>
                <div class="is-padding-top-xsmall">
                  <span>Dernière activité commerciale :&nbsp;</span>
                  <b v-if="c.last_business_activity_date">{{ c.last_business_activity_date | dtFormat('DD/MM/YYYY') }}</b>
                  <b v-else>Aucune</b>
                </div>
              </div>
            </div>
          </div>
          <div
            class="columns is-gapless is-border-top is-marginless">
            <div class="column is-12 is-padding">
              <div class="is-size-6">
                <b class="is-margin-right-xxsmall">Programme(s) associé(s) :</b>
                <i class="fas fa-circle is-black-color icone-size-xs"></i><span class="is-margin-right-xsmall icone-size-xs"> Principal</span>
                <i class="fas fa-circle is-white-color icone-size-xs my-bordered-icon"></i><span class="icone-size-xs"> Secondaire</span>
                <div
                  v-if="(c.programs && c.programs.length) || c.original_program"
                  class="tags is-padding-top-xsmall">
                  <router-link
                    v-if="c.original_program"
                    :to="{ name: 'ProgramVue', params: { id: c.original_program.pk }}"
                    :key="c.original_program.pk">
                    <div
                      class="tag is-primary is-rounded is-margin-right-xxsmall">
                      <span>{{ c.original_program.name }}</span>
                    </div>
                  </router-link>
                  <router-link
                    :to="{name: 'ProgramVue', params: {id: program.pk}}"
                    v-for="program in c.programs"
                    :key="program.pk + program.created_on">
                    <div
                      class="tag is-light has-text-black is-rounded is-margin-right-xxsmall is-border"
                      style="border-color: #24323b;">
                      <span>{{ program.name }}</span>
                    </div>
                  </router-link>
                </div>
                <span v-else>
                  <router-link
                    class="is-underline"
                    :to="{name: 'ContactEdit', params: {id: c.pk}}">
                    Sélectionner
                  </router-link>
                </span>
              </div>
            </div>
          </div>
          <div
            v-if="c.additional_fields && c.additional_fields.length && formated_additional_fields.length"
            class="is-padding is-border-top">
            <div class="columns is-multiline">
              <div class="column is-12 is-paddingless-bottom">
                <div class="columns is-gapless">
                  <div class="column is-size-6 is-narrow  is-margin-right-xsmall">
                    <b>Informations complémentaires :</b>
                  </div>
                </div>
                <div
                  v-for="field in formated_additional_fields"
                  :key="field.name"
                  class="columns is-gapless">
                  <div class="column is-size-6 is-narrow is-margin-right-xsmall">
                    <b>{{ field.name }} :</b>
                  </div>
                  <div
                    v-if="typeof(field.value) !== 'number' && conditions.some(el => field.value.includes(el))"
                    class="column is-size-6 has-text-right">
                    <a
                      :href="field.value"
                      class="is-underline"
                      target="_blank">Voir fiche diffuseur</a>
                  </div>
                  <div
                    v-else
                    class="column is-size-6 has-text-right">
                    {{ field.value }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <event-tracker-list
          v-if="$route.params.id"
          :field="'contact'"
          @logs="c.history = $event" />
      </template>
      <div class="is-border-top">
        <tabs
          :links="contactTabLinks"
          :level="1"
          class="is-border-left is-border-right" />
        <router-view
          :key="$route.name"
          :source-contact="c"
          :source-contact-history="c.history"
          :source-contact-notes="contactNotes"
          :source-contact-options="contactOptions"
          :source-contact-claims="contactClaims"
          :source-contact-rdvs="contactRdvs"
          :source-contact-searches="contactSearches"
          :source-contact-reservations="contactReservations"
          class="has-background-white is-border-left is-border-right is-border-bottom"
          @reloadContact="getContact"
          @editNote="editNote"
          @deleteNote="deleteNote"
          @createNote="createNote"
          @updateSources="updateSources" />
      </div>
    </page-content>
  </div>
</template>

<script>
import ContactMixin from './ContactMixin'
import { mapState, mapGetters } from 'vuex'
import randomColor from 'randomcolor'
import { startCase } from 'lodash'
import moment from 'moment'
import 'vue-swatches/dist/vue-swatches.min.css'

export default {
  name: 'ContactVue',
  components: {
    Swatches: () => import('vue-swatches')
  },
  mixins: [ContactMixin],
  data () {
    return {
      contactSharing: false,
      isParent: true,
      conditions: ['https', 'http', 'www'],
      doublons: {},
      messages: ['Doublon Nom/Prénom', 'Doublon Nom/Prénom et Adresse', 'Doublon Email'],
      messages_2: ['Doublon Nom/Prénom', 'Doublon Nom/Prénom et Adresse', 'Doublon Email'],
      claim_list_pk: []
    }
  },
  computed: {
    ...mapState(['quickview']),
    ...mapGetters(['currentUserPk', 'isAdmin', 'isPromoteur']),
    ...mapGetters({ storeAccounts: 'accounts' }),
    /** Remove fields who dont container name value (for other uses) and convert name to readable case */
    formated_additional_fields () {
      let additionalFields = this.c.additional_fields
      additionalFields = additionalFields
        .filter((field) => ('name' in field && 'value' in field))
        .map(field => {
          return {
            ...field,
            name: startCase(field.name),
            test: field.value
          }
        })
      console.log(additionalFields)

      return additionalFields
    },
    isAdminCgp () {
      return this.isAdmin && this.contact.account.pk !== this.currentUser.account.pk
    },
    isAdminPromoteur () {
      return this.isAdmin && this.contact.account.pk === this.currentUser.account.pk
    },
    canAddUsers () {
      return !!this.plan && this.plan.can_add_users
    },
    canEdit () {
      if (this.c.account.pk === this.currentUser.account.pk) {
        return true
      }
      if (this.c.user && this.c.user.pk === this.currentUser.pk) {
        return true
      }
      if (this.c.user && this.c.user.account.pk === this.currentUser.account.pk) {
        return true
      }
      return false
    }
  },
  watch: {
    quickview ({ mode, params, results }) {
      if (results && mode === 'USER_SELECTION') {
        if (results.entities.length) {
          this.contactUser = results.entities[0].pk
          this.patchContact(this.c, { user: results.entities[0].pk })
        } else {
          this.$toasted.global.danger('Veuillez séléctionner un utilisateur')
        }
      }
      if (results && mode === 'CONTACT_SEARCHES') {
        this.editOrCreateContactSearch(results.search)
      }
      if (results && mode === 'CONTACT_SELECTION') {
        if (results.entities.length) {
          this.addContact(results.entities[0])
        } else {
          this.$toasted.global.danger('Veuillez séléctionner un contact')
        }
      }
      if (results && mode === 'CONTACT_CREATE' && params.spouseMode) {
        this.spouseMode = params.spouseMode
        this.createContact(results.contact)
      }
      if (results && mode === 'NOTE_CREATE') {
        if ('rdvPk' in params && params.rdvPk) {
          this.addRdvNote(results.note, params.rdvPk)
        }
        //  else {
        //   this.editCreateDeleteContactNote(results.note)
        // }
      }
      if (results && mode === 'RELATION_SELECTION' && this.contactSharing) {
        if (results.entities.length) {
          this.checkShareIntent(results.entities[0].pk)
        } else {
          this.$toasted.global.danger('Veuillez séléctionner une société')
        }
      }
    }
  },
  async created () {
    if (!this.contactTabLinks.map(x => x.link).includes(this.contactView)) {
      this.$router.replace({ name: 'ContactHistory' }).catch(() => {})
    }
    await this.getDoublons()
    await this.getClaimContact()
  },
  methods: {
    async updateColor (contact, color) {
      const data = {
        swatch_color: color,
        first_name: contact.first_name,
        last_name: contact.last_name
      }
      try {
        await this.$api.put(this.$resources.CONTACT, contact.pk, data)
      } catch (error) {
        this.$toasted.global.danger('Erreur lors de la mise à jour de la couleur !')
        console.error('Erreur lors de la mise à jour :', error)
      }
    },
    isPromoteurDetailsActivated (c) {
      return this.storeAccounts.find(a => a.name === c.account.name).promoteur_details_activated
    },
    async getClaimContact () {
      if (this.doublons && this.doublons.duplicates && this.doublons.duplicates.length > 0) {
        for (let i of this.doublons.duplicates) {
          let params = {
            status: ['approved', 'pending'],
            contact: i.pk
          }
          let { data } = await this.$api.get(this.$resources.CLAIM, params)
          data.items.forEach(claim => { this.claim_list_pk.push(claim.pk) })
        }
      }
    },
    async stopContactSharing () {
      const value = await this.$swal({
        title: 'Arrêter un partage',
        text: this.stopContactModalContent,
        confirmButtonText: 'Valider',
        focusConfirm: false,
        customClass: {
          confirmButton: 'is-danger'
        }
      })
      if (this.contact.account.pk === this.currentUser.account.pk) {
        if (value.dismiss) return
        if (value) this.patchContact(this.contact, { user: null })
      } else {
        if (value.dismiss) return
        if (value) {
          this.loading = true
          try {
            await this.$api.delete(this.$resources.CONTACT, this.contact.pk)
            this.loading = false
            this.$toasted.global.success('Partage arrếté')
            this.$router.push({ name: 'ContactList' })
          } catch (e) {
            this.loading = false
            this.$toasted.global.danger('Erreur lors de l\'arrêt du partage !')
          }
        }
      }
    },
    async initContactSharing (type = 'multiple') {
      this.contactSharing = true
      const params = {
        searchKeys: 'ACCOUNT',
        selectType: type,
        facets: {
          ACCOUNT: []
        }
      }
      this.openRelationSelectionQuickview(params)
    },
    async askForce (accountPk) {
      let value = await this.$swal({
        title: `Contact assigné`,
        text: `Attention, ce contact est déjà assigné à un utilisateur. Cette action annulera l'assignation à l'utilisateur afin de le partager à une société. Êtes vous sur ?`,
        confirmButtonText: 'Oui',
        cancelButtonText: 'Non',
        focusConfirm: false,
        customClass: {
          confirmButton: 'is-danger'
        }
      })
      if (value.dismiss) return false
      if (value) {
        this.sendShareIntent(accountPk, true)
      }
    },
    async checkShareIntent (accountPk) {
      let validContact = {}
      const payload = {
        account: accountPk,
        contacts: [this.contact.pk],
        check: 1
      }
      try {
        let { data } = await this.$api.post(this.$resources.SHARE_INTENT, payload)
        validContact = Object.assign({}, data.contacts[0])
      } catch (e) {
        console.log(e)
      }
      if (validContact.in_proposal) {
        this.$toasted.global.danger(`Ce contact fait déjà l'objet d'une demande de partage.`)
        this.$store.commit('setQuickview', { mode: null, params: null, results: null, close: true })
        this.contactSharing = false
      } else if (validContact.has_user) {
        this.askForce(accountPk)
      } else {
        this.sendShareIntent(accountPk, false)
      }
    },
    async sendShareIntent (accountPk, force = false) {
      const payload = {
        account: accountPk,
        contacts: [this.contact.pk],
        force
      }
      try {
        let { data } = await this.$api.post(this.$resources.SHARE_INTENT, payload)
        const shareIntentPk = data.pk
        this.$toasted.global.success('Proposition de partage envoyé.')
        await this.$router.push({
          name: 'ShareIntentList',
          query: {
            shareIntentPk
          }
        })
      } catch (e) {
        console.log(e)
        this.$toasted.global.danger('Erreur lors du partage de contact.')
      } finally {
        this.$store.commit('setQuickview', { mode: null, params: null, results: null, close: true })
        this.contactSharing = false
      }
    },
    tagColor (value, hue = 'blue') {
      let color = randomColor({
        seed: value,
        hue
      })
      return color
    },
    updateStatus () {
      this.patchContact(this.contact, { status: this.contact.status })
    },
    updateRgpdConsent () {
      this.patchContact(this.contact, { rgpd_consent: !this.contact.rgpd_consent }, true)
    },
    // RGPD : dernier échange saisi manuellement (date seule, sans heure)
    updateLastInteractionDate (value) {
      const date = value ? moment(value).format('YYYY-MM-DD') : null
      if (date === this.contact.last_interaction_date) return
      this.patchContact(this.contact, { last_interaction_date: date }, true)
    },
    async addRdvNote (note, pk) {
      const payload = {
        note: note.message
      }
      try {
        await this.$api.patch(this.$resources.RDV, pk, payload)
        this.$store.commit('setQuickview', { mode: null, params: null, results: null, close: true })
        this.getContactRdvs()
      } catch (err) {
        console.log(err)
      }
    },
    async addContact (contact) {
      let completeContact = await this.returnContact(contact.pk)
      this.contactSpouse = completeContact.pk
      this.patchContact(this.c, { spouse: completeContact.pk })
      this.$store.commit('setQuickview', { mode: null, params: null, results: null, close: true })
    },
    async returnContact (id) {
      try {
        let { data } = await this.$api.getOne(this.$resources.CONTACT, id)
        return data
      } catch (err) {
        console.log(err)
      }
    },
    async editOrCreateContactSearch (search) {
      try {
        if (search.pk) {
          await this.$api.put(this.$resources.CONTACT_RESEARCH(this.contactPk), search.pk, search)
          this.$toasted.global.success('Recherche du contact modifiée')
        } else {
          await this.$api.post(this.$resources.CONTACT_RESEARCH(this.contactPk), search)
          this.$toasted.global.success('Recherche du contact créée')
        }
        this.$store.commit('setQuickview', { mode: null, params: null, results: null, close: true })
        this.getContactSearches()
      } catch (e) {
        console.log(e)
        this.$toasted.global.danger('Erreur lors de l\'enregistrement !')
      }
    },
    async editNote (note) {
      try {
        await this.$api.put(this.$resources.CONTACT_NOTE(this.contactPk), note.pk, note)
        this.getContactNotes()
      } catch (err) {
        console.log(err)
      }
    },
    async deleteNote (note) {
      try {
        await this.$api.delete(this.$resources.CONTACT_NOTE(this.contactPk), note.pk)
        this.getContactNotes()
      } catch (err) {
        console.log(err)
      }
    },
    async createNote (note) {
      try {
        await this.$api.post(this.$resources.CONTACT_NOTE(this.contactPk), note)
        this.getContactNotes()
      } catch (err) {
        console.log(err)
      }
    },
    async getDoublons () {
      try {
        let completeContact = await this.returnContact(this.contactPk)
        let payload = null
        if (completeContact.user && completeContact.user.pk) {
          payload = {
            contact: completeContact,
            user: completeContact.user.pk
          }
        } else {
          payload = {
            contact: completeContact
          }
        }
        let { data } = await this.$api.post(this.$resources.CONTACT_CHECK, payload)
        if (data.duplicates.length !== 0 || data.all_pk_contact.length !== 0) {
          this.doublons = data
          this.doublons.all_pk_contact.push(this.c.pk)
        }
      } catch (err) {
        console.log(err)
      }
    },
    closeNoteQuickview () {
      this.$store.commit('closeQuickview')
      this.getContactNotes()
    },
    async showDuplicates (doublons) {
      let completeContact = await this.returnContact(this.contactPk)
      if (doublons.duplicate_risk === 1) {
        this.$router.push({ name: 'DenunciationList', query: { contact__searchable_name: `${completeContact.first_name} ${completeContact.last_name}`, status: ['approved', 'pending'] } })
      }
      if (doublons.duplicate_risk === 2) {
        this.$router.push({ name: 'DenunciationList', query: { contact__email: completeContact.email, status: ['approved', 'pending'] } })
      }
      if (doublons.duplicate_risk === 3) {
        this.$router.push({ name: 'DenunciationList', query: { contact__email: completeContact.email, status: ['approved', 'pending'] } })
      }
    }
  }
}
</script>
<style>
.vue-swatches.colorPicker .vue-swatches__trigger {
  display: block;
}

.margin-left {
  margin-left: 6.5rem;
}

.tooltip-text {
  visibility: hidden;
  position: absolute;
  z-index: 1;
  width: 100px;
  color: white;
  font-size: 12px;
  background-color:  #474849;
  border-radius: 10px;
  opacity: 0.80;
  padding: 5px 10px 5px 10px;
}

.hover-text:hover .tooltip-text {
  visibility: visible;
}

#top {
  top: -8px;
  left: 105%;
}

.hover-text {
  position: relative;
  display: inline-block;
  font-family: Roboto;
}

</style>
